// Element data-property mutators — pure (state, args) => patch. `ids` arrays
// throughout, so single-element panel edits and multi-select mass edits are the
// same call. Locked elements are skipped.

// Apply a data patch to every element in `ids`. Ellipse-specific rules:
// width edits keep height in sync (circles on the wire), and canopy styles other
// than smooth need a seed — pass one in (randomness stays with the caller).
export function setElementProps(state, { ids, patch, canopySeed }) {
  const set = new Set(ids);
  if (!set.size || !patch || !Object.keys(patch).length) return null;
  let hit = false;
  const elements = state.elements.map((el) => {
    if (!set.has(el.id) || el.data?.locked) return el;
    hit = true;
    const d = { ...el.data, ...patch };
    if (el.type === 'ellipse') {
      if (patch.width != null) d.height = d.width;
      if (patch.canopyStyle && patch.canopyStyle !== 'smooth' && !d.canopySeed) d.canopySeed = canopySeed ?? 1;
    }
    return { ...el, data: d };
  });
  return hit ? { elements } : null;
}

// Paste a copied style onto the selection (fill/stroke/width + canopy for ellipses).
export function pasteStyle(state, { ids, style }) {
  const set = new Set(ids);
  if (!set.size || !style) return null;
  let hit = false;
  const elements = state.elements.map((el) => {
    if (!set.has(el.id) || el.data?.locked) return el;
    hit = true;
    const d = {
      ...el.data,
      fillColor: style.fillColor,
      strokeColor: style.strokeColor,
      strokeWidth: style.strokeWidth,
    };
    if (el.type === 'ellipse' && style.canopyStyle != null) d.canopyStyle = style.canopyStyle;
    return { ...el, data: d };
  });
  return hit ? { elements } : null;
}

// Width/height edits for an image. Unless its ratio was deliberately unlocked
// (`lockRatio: false`), the other side follows. The ratio comes from the
// image's own size, or — while it's still drawn at its natural size (no
// width/height yet) — from the loaded picture (`natural`). Returns a data patch.
export function imageSizePatch(el, field, value, natural = null) {
  const d = el.data || {};
  if (!(value > 0)) return { [field]: value };
  if (d.lockRatio === false) return { [field]: value };
  const w = d.width || natural?.width; const h = d.height || natural?.height;
  if (!(w > 0) || !(h > 0)) return { [field]: value };
  return field === 'width' ? { width: value, height: value * (h / w) } : { width: value * (w / h), height: value };
}
